import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { deviceAtom } from "store/deviceStore";

/**
 * 화면 하단 고정 버튼이 키보드에 가려지지 않도록 위치 오프셋을 계산해
 * CSS 변수 `--bottom-btn-offset` 에 반영한다.
 *
 * [플랫폼 차이]
 * - Android: AndroidManifest 의 windowSoftInputMode="adjustResize" 로 키보드가 뜨면
 *   웹뷰 자체가 줄어든다. 레이아웃 뷰포트도 함께 작아지므로 bottom:0 이 이미 키보드 위다.
 * - iOS(WKWebView): 키보드가 화면을 덮기만 한다. window.innerHeight 는 그대로이고
 *   visualViewport.height 만 줄어들어, bottom:0 은 키보드 뒤에 가려진다.
 *
 * 두 경우를 아래 한 식으로 처리한다. 플랫폼 분기가 필요 없다.
 *
 *   겹침 = window.innerHeight - (visualViewport.height + visualViewport.offsetTop)
 *
 *   iOS      : innerHeight 고정 + height 축소 → 겹침 = 키보드 높이  → 버튼이 올라감
 *   Android  : 둘 다 축소                     → 겹침 ≈ 0            → 기존 동작 유지
 *
 * [--safe-bottom 을 쓰지 않는 이유]
 * --safe-bottom 은 BottomSheet 등 다른 컴포넌트도 참조한다. 키보드 상태에 따라 전역으로
 * 토글하면 무관한 화면의 여백까지 흔들리므로, 하단 버튼 전용 변수를 따로 둔다.
 *
 * visualViewport 미지원 환경에서는 아무것도 설정하지 않는다.
 * CSS 쪽 fallback 이 기존 --safe-bottom 을 그대로 사용하므로 동작이 유지된다.
 */
export const useKeyboardInset = () => {
    const device = useAtomValue(deviceAtom);
    const navigationBarHeight = device?.inset?.navigationBarHeight ?? 0;

    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        const el = document.documentElement;
        let raf = 0;

        const apply = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                // 버튼을 올려야 할 높이. 브라우저가 화면을 밀어 올리는(pan) 경우 offsetTop 만큼
                // 이미 이동했으므로 그만큼 빼야 이중으로 올라가지 않는다.
                const overlap = Math.max(
                    0,
                    Math.round(window.innerHeight - (vv.height + vv.offsetTop))
                );
                // 키보드 열림 판정은 overlap 이 아니라 '보이는 높이가 줄었는지'로 한다.
                //   pan 방식(현재 Android)은 화면을 밀어 올리므로 overlap 이 0 이지만 키보드는 떠 있다.
                //   overlap 으로 판정하면 닫힘으로 오인해 내비게이션 바 여백이 더해지고,
                //   버튼이 키보드보다 그만큼 위에 뜬다.
                // 주소창 노출/숨김 등 미세한 변동을 키보드로 오인하지 않도록 여유를 둔다.
                const keyboardOpen = window.innerHeight - vv.height > 50;
                // 키보드가 떠 있으면 내비게이션 바는 가려지므로 그 여백은 더하지 않는다.
                const offset = keyboardOpen ? overlap : navigationBarHeight;
                el.style.setProperty("--bottom-btn-offset", `${offset}px`);

                // [해보고 되돌린 것] 래퍼의 min-height 를 키보드 높이만큼 줄여 여백을 없애려 했으나
                // 효과가 없어 제거했다. 레이아웃 뷰포트(innerHeight)는 키보드가 떠도 그대로이고
                // 하단 버튼은 position:fixed 로 그 기준에 붙으므로, 래퍼 상자만 줄여도
                // 내용과 버튼 사이 거리는 변하지 않는다(실측: 닫힘 345px / 열림 393px).
                // 여백까지 없애려면 화면 영역 자체를 visualViewport 높이에 묶어야 하며,
                // 그것은 레이아웃 구조 변경이라 별도 판단이 필요하다.
            });
        };

        apply();
        vv.addEventListener("resize", apply);
        // iOS 는 키보드 전환 중 scroll 로도 뷰포트 변화를 통지한다.
        vv.addEventListener("scroll", apply);

        return () => {
            cancelAnimationFrame(raf);
            vv.removeEventListener("resize", apply);
            vv.removeEventListener("scroll", apply);
        };
    }, [navigationBarHeight]);
};
